import { analytics, EVENTS } from '@/services/analytics';

/**
 * Opening the HubSpot support chat, with every failure named.
 *
 * Both surfaces (the Help Center page and the toolbar dialog) used to carry
 * their own copy of this flow, and both reported failure the same way: a blind
 * 20s `setTimeout` cleared only by HubSpot's `widgetLoaded` event. A blocked
 * script, a rejected identification token, and a slow widget all rendered the
 * same sentence, nothing was tracked, and the backend logged a clean 200 either
 * way. When two enterprise accounts hit this, reconstructing what happened took
 * a log dig per user and still could not separate the causes.
 *
 * So each phase here fails on its own terms, as early as it can, and reports
 * which phase failed.
 */

export const SUPPORT_EMAIL: string = 'support@rundiffusion.com';

/** Which surface the attempt came from, so failures can be split by entry point. */
export const SUPPORT_CHAT_SOURCE = {
	HELP_CENTER: 'help_center',
	TOOLBAR_DIALOG: 'toolbar_dialog',
} as const;

export type SupportChatSource = typeof SUPPORT_CHAT_SOURCE[keyof typeof SUPPORT_CHAT_SOURCE];

/**
 * Why the chat would not open. These are the values of `failure_reason` on
 * `support_chat_open_failed`, so renaming one breaks continuity in PostHog.
 */
export const SUPPORT_CHAT_FAILURE = {
	/** An admin is proxying a user: a ticket would be filed against the wrong person. */
	ADMIN_PROXYING: 'ADMIN_PROXYING',
	/** Our callable answered but had no visitor identification token to give. */
	IDENTIFICATION_TOKEN_MISSING: 'IDENTIFICATION_TOKEN_MISSING',
	/** Our callable did not answer in time. */
	IDENTIFICATION_TOKEN_TIMEOUT: 'IDENTIFICATION_TOKEN_TIMEOUT',
	/** HubSpot's script never downloaded: adblocker, VPN, company proxy, DNS filter. */
	SCRIPT_LOAD_BLOCKED: 'SCRIPT_LOAD_BLOCKED',
	/** The script request did not error, but HubSpot never exposed its API. */
	SCRIPT_LOAD_TIMEOUT: 'SCRIPT_LOAD_TIMEOUT',
	/** HubSpot is present and took the call, but the widget never signalled ready. */
	WIDGET_LOAD_TIMEOUT: 'WIDGET_LOAD_TIMEOUT',
	UNKNOWN: 'UNKNOWN',
} as const;

export type SupportChatFailure = typeof SUPPORT_CHAT_FAILURE[keyof typeof SUPPORT_CHAT_FAILURE];

export const HUBSPOT_SCRIPT_SRC: string = 'https://js.hs-scripts.com/48248774.js';
const HUBSPOT_SCRIPT_ID: string = 'hs-script-loader';

export interface SupportChatTimeouts {
	/** Ceiling on the whole attempt, so per-phase caps cannot stack. */
	overall: number;
	/** Cap for our own callable. */
	token: number;
	/** Cap for HubSpot's script to download and expose `HubSpotConversations`. */
	script: number;
	/** Cap for `widgetLoaded` after the widget has been asked to load. */
	widget: number;
	/** Poll interval while waiting on `window`. */
	poll: number;
}

/**
 * Sized from measurement, not from the Cloud Function logs.
 *
 * `Function execution took Nms` excludes cold-start container boot and callable
 * overhead: an observed first call logged 3991ms server-side but took over 8s
 * round trip, while the warm call right after was 1881ms server-side and 3601ms
 * round trip. A token cap set from the server numbers therefore reports
 * IDENTIFICATION_TOKEN_TIMEOUT for a request that actually succeeded, and tells
 * the user it is our fault when nothing is wrong. Hence 20s for the token.
 *
 * `overall` exists so those caps cannot sum to a 42s wait. Each phase gets
 * whichever is smaller: its own cap, or what is left of the attempt.
 */
export const DEFAULT_SUPPORT_CHAT_TIMEOUTS: SupportChatTimeouts = {
	overall: 25000,
	token: 20000,
	script: 10000,
	widget: 12000,
	poll: 100,
};

export interface SupportChatIdTokenResponse {
	success: boolean;
	token?: string | null;
}

export interface OpenSupportChatOptions {
	source: SupportChatSource;
	email: string;
	teamId: string | null;
	isAdminProxyingUser: boolean;
	/** Calls the `getSupportChatIdToken` callable. Injected so this stays testable. */
	createIdToken: (teamId: string | null) => Promise<SupportChatIdTokenResponse>;
	timeouts?: Partial<SupportChatTimeouts>;
}

/**
 * Deliberately one shape rather than a discriminated union: web compiles with
 * `strictNullChecks: false`, under which TypeScript will not narrow a union by
 * a boolean discriminant, so `if (result.ok) return;` leaves `reason`
 * unreachable at the .vue call sites.
 */
export interface SupportChatResult {
	ok: boolean;
	/** Set only when `ok` is false. */
	reason: SupportChatFailure | null;
}

/**
 * The failure copy. Every message says whether the user can do anything about
 * it, and always names the email fallback, because the whole point of the chat
 * is reaching us and that must not depend on the chat working.
 */
export const getSupportChatErrorMessage = (reason: SupportChatFailure): string => {
	switch (reason) {
		case SUPPORT_CHAT_FAILURE.ADMIN_PROXYING:
			return 'You cannot open a support ticket while pretending as a user.';
		case SUPPORT_CHAT_FAILURE.SCRIPT_LOAD_BLOCKED:
			return `We could not load the support chat because something on your network or browser blocked it. Adblockers, VPNs, and company firewalls are the usual cause. Please email us at ${SUPPORT_EMAIL} instead and we will pick it up from there.`;
		case SUPPORT_CHAT_FAILURE.SCRIPT_LOAD_TIMEOUT:
			return `The support chat did not finish loading. This is often a slow or filtered network connection. Please email us at ${SUPPORT_EMAIL} instead and we will pick it up from there.`;
		case SUPPORT_CHAT_FAILURE.IDENTIFICATION_TOKEN_MISSING:
		case SUPPORT_CHAT_FAILURE.IDENTIFICATION_TOKEN_TIMEOUT:
			// Naming it as ours matters: the old copy blamed the customer's
			// firewall for failures that were not theirs, and they burned time
			// switching browsers because of it.
			return `We could not start a secure support chat session. This one is on our side, not your network. Please email us at ${SUPPORT_EMAIL} and we will pick it up from there.`;
		case SUPPORT_CHAT_FAILURE.WIDGET_LOAD_TIMEOUT:
			return `The support chat loaded but would not open. Please email us at ${SUPPORT_EMAIL} instead and we will pick it up from there.`;
		default:
			return `We could not open the support chat. Please email us at ${SUPPORT_EMAIL} and we will pick it up from there.`;
	}
};

/** A `mailto:` prefilled with what support needs to find the account. */
export const buildSupportMailtoHref = (reason: SupportChatFailure, email: string): string => {
	const subject: string = 'Support request (chat unavailable)';
	const body: string = [
		'Please describe your issue here.',
		'',
		'---',
		'Sent because the in-app support chat would not open.',
		`Account: ${email || 'unknown'}`,
		`Reason code: ${reason}`,
	].join('\n');
	return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const getConversations = (): any => (window as any).HubSpotConversations || null;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolves once HubSpot's API is on `window`.
 *
 * Polling rather than `hsConversationsOnReady`: the script may already be in
 * flight from GTM, in which case the ready queue has its own ordering hazards,
 * and a poll treats "already loaded", "we injected it", and "GTM injected it"
 * identically.
 */
const ensureHubSpotLoaded = async (timeouts: SupportChatTimeouts, budgetMs: number): Promise<SupportChatFailure | null> => {
	if (getConversations()) return null;

	let blocked: boolean = false;
	const alreadyInjected: boolean = !!document.getElementById(HUBSPOT_SCRIPT_ID)
		|| Array.from(document.scripts || []).some((s: HTMLScriptElement) => s?.src === HUBSPOT_SCRIPT_SRC);

	if (!alreadyInjected) {
		const script: HTMLScriptElement = document.createElement('script');
		script.type = 'text/javascript';
		script.id = HUBSPOT_SCRIPT_ID;
		script.src = HUBSPOT_SCRIPT_SRC;
		script.async = true;
		script.defer = true;
		// The signal we never had: a blocked host errors here in well under a
		// second, instead of looking identical to a slow load for 20s.
		script.onerror = () => { blocked = true; };
		document.head.appendChild(script);
	}

	const deadline: number = Date.now() + Math.min(timeouts.script, budgetMs);
	while (Date.now() < deadline) {
		if (getConversations()) return null;
		if (blocked) return SUPPORT_CHAT_FAILURE.SCRIPT_LOAD_BLOCKED;
		await wait(timeouts.poll);
	}
	return getConversations()
		? null
		: (blocked ? SUPPORT_CHAT_FAILURE.SCRIPT_LOAD_BLOCKED : SUPPORT_CHAT_FAILURE.SCRIPT_LOAD_TIMEOUT);
};

/**
 * Binds the `widgetLoaded` / `widgetClosed` listeners exactly once per page.
 * Each attempt swaps the callbacks behind them rather than stacking new
 * closures on HubSpot's emitter.
 */
const ensureGlobalListeners = (): void => {
	if ((window as any).__rdHubspotSupportChatListenersBound) return;
	const conversations: any = getConversations();
	if (!conversations?.on) return;
	(window as any).__rdHubspotSupportChatListenersBound = true;

	conversations.on('widgetLoaded', () => {
		(window as any).__rdSupportChatOnWidgetLoaded?.();
	});
	conversations.on('widgetClosed', () => {
		// Removing on close is what makes the next `load()` re-fire
		// `widgetLoaded`; without it a second attempt can never be confirmed.
		getConversations()?.widget?.remove?.();
	});
};

const loadAndOpenWidget = async (timeouts: SupportChatTimeouts, budgetMs: number): Promise<SupportChatFailure | null> => {
	const conversations: any = getConversations();
	if (!conversations) return SUPPORT_CHAT_FAILURE.UNKNOWN;

	ensureGlobalListeners();

	// An already-loaded widget will not emit `widgetLoaded` again, so waiting on
	// it would report a timeout for a chat that opened fine.
	const alreadyLoaded: boolean = !!conversations.widget?.status?.()?.loaded;
	if (alreadyLoaded) {
		conversations.widget?.open?.();
		return null;
	}

	let loaded: boolean = false;
	(window as any).__rdSupportChatOnWidgetLoaded = () => { loaded = true; };

	conversations.widget?.load?.();
	conversations.widget?.open?.();

	const deadline: number = Date.now() + Math.min(timeouts.widget, budgetMs);
	while (Date.now() < deadline) {
		if (loaded) return null;
		await wait(timeouts.poll);
	}
	return loaded ? null : SUPPORT_CHAT_FAILURE.WIDGET_LOAD_TIMEOUT;
};

/**
 * Opens the support chat, emitting one `support_chat_open_attempted` and
 * exactly one terminal event so the failure rate is a ratio, not a raw count.
 */
export const openSupportChat = async (options: OpenSupportChatOptions): Promise<SupportChatResult> => {
	const timeouts: SupportChatTimeouts = { ...DEFAULT_SUPPORT_CHAT_TIMEOUTS, ...(options.timeouts || {}) };
	const startedAt: number = Date.now();
	const remaining = (): number => Math.max(0, timeouts.overall - (Date.now() - startedAt));

	analytics.track(EVENTS.SUPPORT_CHAT_OPEN_ATTEMPTED, { source: options.source });

	const fail = (reason: SupportChatFailure): SupportChatResult => {
		analytics.track(EVENTS.SUPPORT_CHAT_OPEN_FAILED, {
			source: options.source,
			failure_reason: reason,
			duration_ms: Date.now() - startedAt,
		});
		return { ok: false, reason };
	};
	const succeed = (): SupportChatResult => {
		analytics.track(EVENTS.SUPPORT_CHAT_OPEN_SUCCEEDED, {
			source: options.source,
			duration_ms: Date.now() - startedAt,
		});
		return { ok: true, reason: null };
	};

	if (options.isAdminProxyingUser) return fail(SUPPORT_CHAT_FAILURE.ADMIN_PROXYING);

	// Clear a widget left over from a previous attempt so this one starts clean.
	getConversations()?.widget?.close?.();

	let token: string = null;
	try {
		const timedOut: symbol = Symbol('support-chat-token-timeout');
		const response: SupportChatIdTokenResponse | symbol = await Promise.race([
			options.createIdToken(options.teamId),
			wait(Math.min(timeouts.token, remaining())).then(() => timedOut),
		]);
		if (response === timedOut) return fail(SUPPORT_CHAT_FAILURE.IDENTIFICATION_TOKEN_TIMEOUT);
		const body: SupportChatIdTokenResponse = response as SupportChatIdTokenResponse;
		if (body?.success && body?.token) token = body.token;
	} catch (e) {
		console.error('[supportChat] identification token request failed', e);
	}

	// Deliberately no anonymous fallback. HubSpot refuses to load an identified
	// widget without a matching token, and an unidentified chat would strand the
	// conversation on a visitor record we cannot tie back to the account.
	if (!token) return fail(SUPPORT_CHAT_FAILURE.IDENTIFICATION_TOKEN_MISSING);

	// Settings must exist before HubSpot's script evaluates.
	const existingSettings: any = (window as any).hsConversationsSettings || {};
	(window as any).hsConversationsSettings = {
		...existingSettings,
		loadImmediately: false,
		identificationEmail: options.email,
		identificationToken: token,
	};

	const scriptFailure: SupportChatFailure = await ensureHubSpotLoaded(timeouts, remaining());
	if (scriptFailure) return fail(scriptFailure);

	const widgetFailure: SupportChatFailure = await loadAndOpenWidget(timeouts, remaining());
	if (widgetFailure) return fail(widgetFailure);

	return succeed();
};
